/**
 * DocGroupCoverageCard — the Documents tab's DocGroup Coverage content for ONE
 * group, wrapped in a self-contained card.
 *
 * It is the coverage board minus the board: the group title, the "n of m" types
 * covered pill, the type rows (teal dot = a file is on file, grey = nothing
 * uploaded), the inline file list per type, the hover upload pill on an empty
 * type, and click-to-preview on every file. Everything comes from
 * shared/docGroupCoverage.js, so a card placed anywhere in the drilldown and the
 * Documents tab itself can't drift apart.
 *
 * Upload: inside an UploadQueueProvider, files stage into the shared bottom
 * queue with docGroup/docType already set. Outside one, `useCoverageUpload`
 * opens the global upload modal with the chosen files already queued and
 * classified — so a card is safe to drop into a rail that has no queue mounted.
 *
 * Record-scoped use (the Applicants tab's card): pass `docs` to count only that
 * record's files, `assignTo` so uploads land pre-assigned to it, and
 * `onAssign` to add "Assign existing" beside every upload action. `openTypes` +
 * `onToggleType` make the per-type expansion controlled, so a parent can
 * expand / collapse every card at once. The group itself folds from its
 * header; `open` + `onToggleOpen` make that controlled too.
 *
 * Callers: the Contacts tab's General Contractor card, the Entity tab's rail,
 * the Applicants tab's card.
 */
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import KeyboardArrowDownRounded from "@mui/icons-material/KeyboardArrowDownRounded";
import { Box, Tooltip } from "@mui/material";
import PropTypes from "prop-types";
import { useCallback, useState } from "react";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { slate, teal } from "../../../theme/colors";
import {
  TypeRow,
  useCoveragePreview,
  useCoverageUpload,
  useDocGroupCoverage,
} from "./docGroupCoverage";

const EMPTY_SET = new Set();

const DocGroupCoverageCard = ({
  group,
  title,
  hideEmpty = false,
  docs,
  assignTo,
  onAssign,
  openTypes: controlledOpenTypes,
  onToggleType,
  open: controlledOpen,
  onToggleOpen,
  sx,
}) => {
  const { types, withDocs, total } = useDocGroupCoverage(group, docs);
  const { uploadTo, inputRef, onInputChange } = useCoverageUpload(assignTo);
  const onPreview = useCoveragePreview();

  // Open state per doc type — same behaviour as the coverage board, local to the
  // card so it doesn't leak into the Documents tab's own expansion state.
  const [localOpenTypes, setOpenTypes] = useState(() => new Set());
  const controlled = typeof onToggleType === "function";
  const openTypes = controlled
    ? controlledOpenTypes || EMPTY_SET
    : localOpenTypes;
  const localToggle = useCallback((name) => {
    setOpenTypes((prev) => {
      const next = new Set(prev);
      next.has(name) ? next.delete(name) : next.add(name);
      return next;
    });
  }, []);
  const toggleType = controlled ? onToggleType : localToggle;

  // The group card folds from its header — local unless the parent controls it.
  const [localOpen, setLocalOpen] = useState(true);
  const groupControlled = typeof onToggleOpen === "function";
  const groupOpen = groupControlled ? controlledOpen !== false : localOpen;
  const toggleGroup = groupControlled
    ? onToggleOpen
    : () => setLocalOpen((v) => !v);

  const complete = total > 0 && withDocs === total;
  // The count pill always reports the WHOLE group — it is the coverage figure,
  // and it would be meaningless if the filter moved it.
  const visibleTypes = hideEmpty
    ? types.filter((t) => t.files.length > 0)
    : types;

  return (
    <Box
      sx={{
        border: `1px solid ${slate[200]}`,
        borderRadius: "10px",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        minWidth: 0,
        ...sx,
      }}
    >
      <Flx
        ac
        jb
        g={1}
        role="button"
        tabIndex={0}
        aria-expanded={groupOpen}
        onClick={toggleGroup}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            toggleGroup();
          }
        }}
        sx={{
          px: 1.75,
          py: 1.25,
          cursor: "pointer",
          outline: "none",
          borderRadius: groupOpen ? "10px 10px 0 0" : "10px",
          borderBottom: groupOpen ? `1px solid ${slate[200]}` : "none",
          "&:hover": { background: slate[50] },
          "&:focus-visible": { boxShadow: `0 0 0 2px ${teal[300]}` },
        }}
      >
        <Flx ac g={1} sx={{ minWidth: 0 }}>
          <DescriptionOutlined sx={{ fontSize: 16, color: slate[400] }} />
          <Txt
            sx={{
              fontSize: 12,
              fontWeight: 600,
              color: slate[900],
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {title || group}
          </Txt>
        </Flx>
        {/* Count + chevron stay together at the right edge. */}
        <Flx ac g={0.5} sx={{ flex: "none" }}>
          <Tooltip
            title={
              complete
                ? "Every document type in this group has a file"
                : `${total - withDocs} document ${
                    total - withDocs === 1 ? "type has" : "types have"
                  } nothing uploaded yet`
            }
          >
            <Box
              component="span"
              sx={{
                flex: "none",
                fontSize: 11,
                fontWeight: 700,
                borderRadius: "999px",
                px: 1.125,
                py: "2px",
                color: complete ? teal[700] : slate[600],
                background: complete ? teal[100] : slate[100],
              }}
            >
              {withDocs} of {total}
            </Box>
          </Tooltip>
          <KeyboardArrowDownRounded
            sx={{
              fontSize: 16,
              color: slate[400],
              flex: "none",
              transition: "transform .15s",
              transform: groupOpen ? "none" : "rotate(-90deg)",
            }}
          />
        </Flx>
      </Flx>

      {groupOpen && (
        <Box sx={{ px: 0.875, py: 0.875, flexGrow: 1 }}>
          {total === 0 ? (
            <Txt sx={{ fontSize: 12.5, color: slate[400], px: 1, py: 1 }}>
              No document types are catalogued for this group.
            </Txt>
          ) : visibleTypes.length === 0 ? (
            <Txt sx={{ fontSize: 12.5, color: slate[400], px: 1, py: 1 }}>
              Nothing uploaded in this group yet.
            </Txt>
          ) : (
            visibleTypes.map((type) => (
              <TypeRow
                key={type.name}
                type={type}
                open={openTypes.has(type.name)}
                onToggle={() => toggleType(type.name)}
                onUpload={(t) => uploadTo(group, t.name)}
                onAssign={onAssign ? (t) => onAssign(group, t.name) : undefined}
                onPreview={onPreview}
              />
            ))
          )}
        </Box>
      )}

      <input
        ref={inputRef}
        type="file"
        multiple
        hidden
        onChange={onInputChange}
      />
    </Box>
  );
};

DocGroupCoverageCard.propTypes = {
  /** The doc GROUP key read from the catalog. */
  group: PropTypes.string.isRequired,
  /** Display heading; defaults to the group key. */
  title: PropTypes.string,
  /** Drop document types with no file, leaving only what has been uploaded. */
  hideEmpty: PropTypes.bool,
  /** Count only these documents (one record's files); defaults to the loan. */
  docs: PropTypes.array,
  /** Pre-assign uploads to this record: { type, id, label }. */
  assignTo: PropTypes.object,
  /** (group, docType) => void — adds "Assign existing" beside each upload. */
  onAssign: PropTypes.func,
  /** Controlled expansion: the open doc-type names, with onToggleType. */
  openTypes: PropTypes.instanceOf(Set),
  onToggleType: PropTypes.func,
  /** Controlled group fold (defaults open), with onToggleOpen. */
  open: PropTypes.bool,
  onToggleOpen: PropTypes.func,
  sx: PropTypes.object,
};

export default DocGroupCoverageCard;
